/* eslint-disable no-await-in-loop */
import { type ComponentType, lazy } from 'react'

import getGlobal from '../getGlobal'
import createLogger from '../Logging'
import safeStorage from '../safeStorage'

const logger = createLogger('lazyWithRetries')

const errorMatch = 'Failed to fetch dynamically imported module: '
const RELOADED_CHUNK_KEY = 'aroyaChunkReloaded'

type Importer<T extends ComponentType<never>> = () => Promise<{ default: T }>

export const lazyWithRetries = <T extends ComponentType<never>>(importer: Importer<T>) => {
  const retryImport = async () => {
    try {
      return await importer()
    } catch (error) {
      if (!(error instanceof Error) || !error.message.includes(errorMatch)) throw error
      let errorToThrow: unknown = error
      // retry 5 times with 2 second delay and backoff factor of 2 (2, 4, 8, 16, 32 seconds)
      for (let i = 0; i < 5; i += 1) {
        await new Promise(resolve => {
          setTimeout(resolve, 250 * 2 ** i)
        })
        // this assumes that the exception will contain this specific text with the url of the module
        // if not, the url will not be able to parse and we'll get an error on that
        // eg. "Failed to fetch dynamically imported module: https://example.com/assets/Home.tsx"
        const url = /https?:\/\/\w+/.test(error.message) ? new URL(error.message.replace(errorMatch, '').trim()) : null
        if (url) {
          // add a timestamp to the url to force a reload the module (and not use the cached version - cache busting)
          url.searchParams.set('t', `${+new Date()}`)

          try {
            return await import(/* @vite-ignore */ url.href)
          } catch (e) {
            errorToThrow = e
            console.error(e)
            logger.debug('retrying import after', 250 * 2 ** (i + 1), 'miliseconds')
          }
        }
      }

      // A chunk that never downloads usually means this tab runs a build whose files were replaced by a
      // newer deploy. One reload fetches the current build; remembering the chunk that caused it keeps the
      // same missing chunk from looping, while a later deploy's chunk can still trigger a reload.
      const chunk = error.message.replace(errorMatch, '').trim()
      const session = safeStorage('session')
      if (session.getItem(RELOADED_CHUNK_KEY) !== chunk) {
        session.setItem(RELOADED_CHUNK_KEY, chunk)
        getGlobal()?.location.reload()
        // Keep Suspense on its fallback until the reload lands, instead of flashing the error page.
        return new Promise<never>(() => {})
      }
      throw errorToThrow
    }
  }
  return Object.assign(lazy(retryImport), { $importer: importer })
}
